import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { findValidUser } from '../utils/firebaseUtils';
import { useSessionNavigation } from '../hooks/useSessionNavigation';
import { soundManager } from '../utils/soundUtils';
import bookmarkImg from '../assets/images/05tale-bookmark.png';
import bookmarkPopImg from '../assets/images/bookmark-pop.png';
import closeBtnImg from '../assets/images/03contract-close-btn.png';
import iosTextImg from '../assets/images/03contract-ios-txt.png';
import returnToStartBtnImg from '../assets/images/returnToStart-btn.png';
import returnToStartPopImg from '../assets/images/returnToStart-popup.png';
import cancelBtnImg from '../assets/images/03contract-no.png';
import okBtnImg from '../assets/images/03contract-yes.png';
import goToEndingBtn from '../assets/images/goToEnding-btn.png';
import ReceivedMessages from './ReceivedMessages';
import SentMessages from './SentMessages';
import ProgressCard from './ProgressCard';

interface BookmarkProps {
  visible: number;
  showPopup?: boolean; // 외부에서 팝업 상태 제어
  onPopupChange?: (show: boolean) => void; // 팝업 상태 변경 콜백
  onProgressClick?: () => void; // 진행상황 클릭 콜백
  zIndex?: number; // z-index 값
  disableProgress?: boolean; // 진행상황 메뉴 비활성화 여부
  showGoToEndingButton?: boolean; // 시나리오 종료하기 버튼 표시 여부
  onGoToEndingClick?: () => void; // 시나리오 종료하기 버튼 클릭 콜백
  customBookmarkImage?: string; // 커스텀 북마크 이미지
}

const BookmarkContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isDragging'].includes(prop)
})<{ zIndex?: number; isDragging?: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  z-index: ${({ zIndex }) => zIndex || 13000}; /* ProgressCard(12000)보다 높게 설정 */
  /* GPU 레이어 분리 및 위치 고정 */
  will-change: ${({ isDragging }) => isDragging ? 'transform' : 'auto'};
  transform: translateZ(0); /* GPU 레이어 강제 생성 */
  isolation: isolate; /* 새로운 stacking context 생성 */
  /* 드래그 중일 때 추가 최적화 */
  ${({ isDragging }) => isDragging && `
    pointer-events: none; /* 드래그 중에는 상호작용 방지 */
    transition: none; /* 드래그 중에는 transition 비활성화 */
  `}
`;

const BookmarkImage = styled.img<{ visible: number }>`
  position: absolute;
  top: 4dvw;
  right: 4.2dvw;
  width: 8.5vw;
  opacity: ${({ visible }) => visible};
  transition: opacity 1.2s ease;
  cursor: pointer;
  /* 북마크 이미지 위치 고정 */
  will-change: auto;
  backface-visibility: hidden;
`;

const PopupOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => !['visible'].includes(prop)
})<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100dvw;
  height: 100dvh;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20000;
  opacity: ${({ visible }) => visible ? 1 : 0};
  visibility: ${({ visible }) => visible ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  pointer-events: ${({ visible }) => visible ? 'auto' : 'none'};
`;

const PopupContainer = styled.div`
  position: relative;
  width: 95dvw;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PopupImage = styled.img`
  width: 100%;
  height: auto;
`;

const PopupContent = styled.div`
  position: absolute;
  top: 28%;
  left: 20%;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

const UserInfo = styled.div`
  font-family: 'MapoDPP', sans-serif;
  font-size: 4dvw;
  color: #FFD700;
  margin-bottom: 3dvw;
  text-align: left;
`;

const CoinInfo = styled.div`
  font-family: 'MapoDPP', sans-serif;
  font-size: 4dvw;
  color: #FFFFFF;
  margin-bottom: 3dvw;
  text-align: left;
  span {
    color: #FFD700;
  }
`;

const MenuItem = styled.div<{ disabled?: boolean }>`
  font-family: 'MapoDPP', sans-serif;
  font-size: 4dvw;
  color: ${({ disabled }) => disabled ? '#A3DAFF' : '#FFFFFF'};
  margin-bottom: 3dvw;
  text-align: left;
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  transition: color 0.2s ease;
  
  &:hover {
    color: ${({ disabled }) => disabled ? '#A3DAFF' : '#FFD700'};
  }
`;

const TimerInfo = styled.div`
  font-family: 'MapoDPP', sans-serif;
  font-size: 4dvw;
  color: #FFFFFF;
  text-align: center;
  position: absolute;
  ;
  top : calc(47% + 1dvw);
  left: 30%;
  transform: translateX(-50%);
`;

const CloseButton = styled.img`
  position: absolute;
  top: -5dvw;
  right: 8vw;
  width: 6vw;
  height: auto;
  cursor: pointer;
  z-index: 10001;
`;

// 계약서 이미지 뷰어 오버레이 스타일
const ContractViewerOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => !['visible'].includes(prop)
})<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100dvw;
  height: 100dvh;
  background: rgba(0, 0, 0, 1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 30000;
  opacity: ${({ visible }) => visible ? 1 : 0};
  visibility: ${({ visible }) => visible ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const ContractViewerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3dvh;
  padding: 4dvw;
  max-width: 90dvw;
`;

const ContractViewerCloseButton = styled.img`
  width: 6dvw;
  height: 6dvw;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-end;
  
  &:hover {
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const ContractViewerImage = styled.img`
  width: 80dvw;
  max-width: 75dvw;
  height: auto;
  border-radius: 1dvw;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
`;

const ContractViewerTextImage = styled.img`
  width: 70dvw;
  max-width: 400px;
  height: auto;
`;

// 처음으로 돌아가기 관련 스타일 컴포넌트
const ReturnToStartButton = styled.img`
  width: 32dvw;
  max-width: 200px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: absolute;
  top: 54%;
  left: 31%;
  transform: translateX(-50%);
  
  &:hover {
    transform: translateX(-50%) scale(1.05);
  }
  
  &:active {
    transform: translateX(-50%) scale(0.95);
  }
`;

const ReturnToStartPopupOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => !['visible'].includes(prop)
})<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100dvw;
  height: 100dvh;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 40000;
  opacity: ${({ visible }) => visible ? 1 : 0};
  visibility: ${({ visible }) => visible ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const ReturnToStartPopupContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3dvh;
  padding: 4dvw;
  max-width: 90dvw;
`;

const ReturnToStartPopupImage = styled.img`
  width: 90dvw;
  max-width: 400px;
  height: auto;
`;

const ReturnToStartButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 2dvw;
`;

const ReturnToStartNoButton = styled.img`
  width: 40dvw;
  max-width: 45dvw;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover { transform: scale(1.05); }
  &:active { transform: scale(0.95); }
`;

const ReturnToStartYesButton = styled.img`
  width: 40dvw;
  max-width: 45dvw;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover { transform: scale(1.05); }
  &:active { transform: scale(0.95); }
`;

const Bookmark: React.FC<BookmarkProps> = ({ visible, showPopup: externalShowPopup, onPopupChange, onProgressClick, zIndex, disableProgress = false, showGoToEndingButton = false, onGoToEndingClick, customBookmarkImage }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [isProgressCardDragging, setIsProgressCardDragging] = useState(false);
  const [showReceivedMessages, setShowReceivedMessages] = useState(false);
  const [showSentMessages, setShowSentMessages] = useState(false);
  const [showProgressCard, setShowProgressCard] = useState(false);
  const [showContractViewer, setShowContractViewer] = useState(false);
  const [showReturnToStartPopup, setShowReturnToStartPopup] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [remainingTime, setRemainingTime] = useState({ minutes: 0, seconds: 0 });
  const { navigateWithSession } = useSessionNavigation();

  // 퀴즈 페이지의 종료하기/SKIP 버튼 클릭 이벤트 감지
  useEffect(() => {
    const handleQuizExit = () => {
      if (showProgressCard) {
        setShowProgressCard(false);
      }
    };

    const handleQuizSkip = () => {
      if (showProgressCard) {
        setShowProgressCard(false);
      }
    };

    window.addEventListener('quizExit', handleQuizExit);
    window.addEventListener('quizSkip', handleQuizSkip);

    return () => {
      window.removeEventListener('quizExit', handleQuizExit);
      window.removeEventListener('quizSkip', handleQuizSkip);
    };
  }, [showProgressCard]);

  // ProgressCard 드래그 이벤트 리스너
  useEffect(() => {
    const handleProgressCardDragStart = () => {
      setIsProgressCardDragging(true);
    };
    
    const handleProgressCardDragEnd = () => {
      setIsProgressCardDragging(false);
    };

    window.addEventListener('progressCardDragStart', handleProgressCardDragStart);
    window.addEventListener('progressCardDragEnd', handleProgressCardDragEnd);

    return () => {
      window.removeEventListener('progressCardDragStart', handleProgressCardDragStart);
      window.removeEventListener('progressCardDragEnd', handleProgressCardDragEnd);
    };
  }, []);

  // 외부에서 팝업 상태가 제어되는 경우
  const isPopupControlled = externalShowPopup !== undefined;
  const currentShowPopup = isPopupControlled ? externalShowPopup : showPopup;

  const handleShowPopup = (show: boolean) => {
    if (isPopupControlled) {
      onPopupChange?.(show);
    } else {
      setShowPopup(show);
    }
  };

  // 사용자 데이터 가져오기
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const currentUser = localStorage.getItem('currentUser');
        if (!currentUser) return;
        
        const userData = JSON.parse(currentUser);
        const firebaseUserData = await findValidUser(userData.nickname, userData.avatarNumber);
        
        if (firebaseUserData) {
          setUserData(firebaseUserData);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, []);

  // 타이머 설정
  useEffect(() => {
    if (!userData?.createdAt) return;

    const calculateRemainingTime = () => {
      const createdAt = userData.createdAt.toDate ? userData.createdAt.toDate() : new Date(userData.createdAt);
      const endTime = new Date(createdAt.getTime() + (2 * 60 * 60 * 1000)); // 2시간 후
      const now = new Date();
      const diff = endTime.getTime() - now.getTime();
      
      if (diff <= 0) {
        setRemainingTime({ minutes: 0, seconds: 0 });
        return;
      }
      
      const minutes = Math.floor(diff / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setRemainingTime({ minutes, seconds });
    };

    calculateRemainingTime();
    const interval = setInterval(calculateRemainingTime, 1000);

    return () => clearInterval(interval);
  }, [userData]);

  const handleBookmarkClick = () => {
    soundManager.playButtonClick();
    handleShowPopup(true);
  };

  const handleClosePopup = () => {
    soundManager.playButtonClick();
    handleShowPopup(false);
  };

  // 배후성 클릭 시 계약서 이미지 표시
  const handleBackgroundClick = () => {
    const contractImage = localStorage.getItem('contractImage');
    if (contractImage) {
      // 사파리 호환성을 위해 이미지 로드 테스트
      const img = new Image();
      img.onload = () => {
        handleShowPopup(false);
        setShowContractViewer(true);
      };
      img.onerror = () => {
        console.error('계약서 이미지 로드 실패 (사파리 호환성 문제)');
        alert('계약서 이미지를 불러올 수 없습니다. 다시 저장해주세요.');
      };
      img.src = contractImage;
    } else {
      alert('계약서 이미지가 없습니다.');
    }
  };

  const handleProgressClick = () => {
    if (disableProgress) return; // 비활성화 상태면 클릭 무시
    soundManager.playButtonClick();
    
    handleShowPopup(false);
    // 진행상황은 현재 페이지 위에 오버레이로 표시
    setShowProgressCard(true);
  };

  const handleMessageClick = () => {
    soundManager.playButtonClick();
    handleShowPopup(false);
    setShowReceivedMessages(true);
  };

  const handleSentMessageClick = () => {
    soundManager.playButtonClick();
    handleShowPopup(false);
    setShowSentMessages(true);
  };

  const handleReceivedMessagesClose = () => {
    setShowReceivedMessages(false);
    handleShowPopup(true); // 메시지 컴포넌트를 닫을 때 인물정보창 다시 열기
  };

  const handleSentMessagesClose = () => {
    setShowSentMessages(false);
    handleShowPopup(true); // 메시지 컴포넌트를 닫을 때 인물정보창 다시 열기
  };

  const handleProgressCardClose = () => {
    setShowProgressCard(false);
  };

  const handleContractViewerClose = () => {
    setShowContractViewer(false);
    // 계약서 뷰어를 닫으면 다시 북마크 팝업 표시
    handleShowPopup(true);
  };

  // 처음으로 돌아가기 버튼 클릭
  const handleReturnToStartClick = () => {
    soundManager.playButtonClick();
    setShowReturnToStartPopup(true);
  };

  // 처음으로 돌아가기 팝업에서 "아니오" 클릭
  const handleReturnToStartNo = () => {
    soundManager.playButtonClick();
    setShowReturnToStartPopup(false);
  };

  // 처음으로 돌아가기 팝업에서 "네" 클릭
  const handleReturnToStartYes = () => {
    soundManager.playButtonClick();
    // 처음으로 돌아가기 사용 기록 저장
    localStorage.setItem('returnToStartUsed', 'true');
    
    // 로컬스토리지에서 처음으로 돌아가기 관련 데이터만 제외하고 나머지 초기화
    const returnToStartUsed = localStorage.getItem('returnToStartUsed');
    localStorage.clear();
    if (returnToStartUsed) {
      localStorage.setItem('returnToStartUsed', returnToStartUsed);
    }
    
    // guide01 페이지로 이동
    navigateWithSession('/guide01');
  };

  // 처음으로 돌아가기 버튼 사용 여부 확인
  const isReturnToStartUsed = () => {
    return localStorage.getItem('returnToStartUsed') === 'true';
  };

  // 로컬스토리지에서 사용자 배후성 정보 가져오기
  const getCurrentUserBackground = () => {
    try {
      const currentUser = localStorage.getItem('currentUser');
      if (currentUser) {
        const userData = JSON.parse(currentUser);
        return userData.background || '없음';
      }
    } catch (error) {
      console.error('Error parsing current user data:', error);
    }
    return '없음';
  };

  // 받은 메시지 컴포넌트가 표시되면 다른 내용을 숨김
  if (showReceivedMessages) {
    return <ReceivedMessages onClose={handleReceivedMessagesClose} />;
  }

  // 보낸 메시지 컴포넌트가 표시되면 다른 내용을 숨김
  if (showSentMessages) {
    return <SentMessages onClose={handleSentMessagesClose} />;
  }

  // ProgressCard가 표시되면 ProgressCard만 렌더링 (현재 페이지 위 오버레이)
  if (showProgressCard) {
    return <ProgressCard 
      fromBookmark={true} 
      onClose={handleProgressCardClose}
    />;
  }

  return (
    <>
      <BookmarkContainer zIndex={zIndex} isDragging={isProgressCardDragging}>
        <BookmarkImage 
          src={customBookmarkImage || bookmarkImg} 
          alt="북마크" 
          visible={visible} 
          onClick={handleBookmarkClick}
        />
      </BookmarkContainer>

      <PopupOverlay visible={currentShowPopup}>
        <PopupContainer>
          <PopupImage src={bookmarkPopImg} alt="북마크 팝업" />
          <PopupContent>
            <UserInfo>
              <span style={{ color: '#FFFFFF' }}>1. 나의 화신 정보</span><br />
              [ {userData?.nickname} / 화신 {userData?.avatarNumber} ]
            </UserInfo>
            
            <CoinInfo>
              2. 보유 코인 : <span>{userData?.coinCount || 0}</span>
            </CoinInfo>
            
            <MenuItem onClick={handleBackgroundClick}>
              3. 배후성: <span style={{ color: '#FFD700' }}>{getCurrentUserBackground()}</span>
            </MenuItem>
            
            <MenuItem onClick={handleProgressClick} disabled={disableProgress}>
              4. 시나리오 진행 현황
            </MenuItem>
            
            <MenuItem onClick={handleMessageClick}>
              5. 받은 메시지
            </MenuItem>
            
            {/* 별자리 연회 페이지에 접근한 후에만 보낸 메시지 표시 */}
            {userData?.zoneClearStatus?.zone6 !== '아직안품' && (
              <MenuItem onClick={handleSentMessageClick}>
                6. 보낸 메시지
              </MenuItem>
            )}
            
            <TimerInfo>
              시나리오 종료 시간까지 {String(remainingTime.minutes).padStart(2, '0')}분 {String(remainingTime.seconds).padStart(2, '0')}초
            </TimerInfo>
            
            {/* 시나리오 종료하기 버튼 또는 처음으로 돌아가기 버튼 */}
            {showGoToEndingButton ? (
              <ReturnToStartButton 
                src={goToEndingBtn} 
                alt="시나리오 종료하기" 
                onClick={() => {
                  handleShowPopup(false); // 팝업 먼저 닫기
                  onGoToEndingClick?.(); // 그 다음 시퀀스 시작
                }}
              />
            ) : (
              !isReturnToStartUsed() && (
                <ReturnToStartButton 
                  src={returnToStartBtnImg} 
                  alt="처음으로 돌아가기" 
                  onClick={handleReturnToStartClick}
                />
              )
            )}
          </PopupContent>
          
          <CloseButton src={closeBtnImg} alt="닫기" onClick={handleClosePopup} />
        </PopupContainer>
      </PopupOverlay>

      {/* 계약서 이미지 뷰어 */}
      <ContractViewerOverlay visible={showContractViewer}>
        <ContractViewerContainer>
          <ContractViewerCloseButton 
            src={closeBtnImg} 
            alt="닫기" 
            onClick={handleContractViewerClose} 
          />
          {(() => {
            const contractImage = localStorage.getItem('contractImage');
            if (contractImage) {
              return (
                <ContractViewerImage 
                  src={contractImage} 
                  alt="계약서"
                  onError={(e) => {
                    console.error('계약서 이미지 표시 실패 (사파리 호환성 문제)');
                    e.currentTarget.style.display = 'none';
                    const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                    if (nextElement) {
                      nextElement.style.display = 'flex';
                    }
                  }}
                />
              );
            }
            return null;
          })()}
          <div style={{ 
            width: '80dvw', 
            maxWidth: '75dvw', 
            height: '60dvh', 
            backgroundColor: '#333', 
            borderRadius: '1dvw', 
            display: localStorage.getItem('contractImage') ? 'none' : 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: '#fff',
            fontSize: '4dvw',
            fontFamily: 'MapoDPP, sans-serif',
            textAlign: 'center',
            padding: '2dvw'
          }}>
            계약서 이미지가 없습니다.<br />
          </div>
          <ContractViewerTextImage 
            src={iosTextImg} 
            alt="계약서 저장 안내" 
          />
        </ContractViewerContainer>
      </ContractViewerOverlay>

      {/* 처음으로 돌아가기 확인 팝업 */}
      <ReturnToStartPopupOverlay visible={showReturnToStartPopup}>
        <ReturnToStartPopupContainer>
          <ReturnToStartPopupImage src={returnToStartPopImg} alt="처음으로 돌아가기 확인" />
          <ReturnToStartButtonRow>
            <ReturnToStartNoButton 
              src={cancelBtnImg} 
              alt="아니오" 
              onClick={handleReturnToStartNo} 
            />
            <ReturnToStartYesButton 
              src={okBtnImg} 
              alt="네" 
              onClick={handleReturnToStartYes} 
            />
          </ReturnToStartButtonRow>
        </ReturnToStartPopupContainer>
      </ReturnToStartPopupOverlay>
    </>
  );
};

export default Bookmark; 